import { KindSelectState } from "../../components/antd/kindselect/KindSelect";
import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import { VolumeSetCreate } from "../../schema/types/volumeSet/create";
import { VolumeSet } from "../../schema/types/volumeSet/entity";
import { VolumeSetInput } from "../../schema/types/volumeSet/input";
import { linksOf } from "../../services/cpln";
import { defaultValues } from "../../services/defaults";
import { store } from "../../store";
import { selectContext } from "../../store/contextSlice";
import { parseLink } from "../../utils/linkParser/linkParser";
import { v4 as uuidv4 } from "uuid";

export function getDefaultVolumeSetInput(): VolumeSetInput {
  const df = defaultValues.volumeset;

  const gvc: KindSelectState = {
    value: "",
    link: "",
  };

  const context = selectContext(store.getState());

  if (context.gvc) {
    const link = `/org/${context.org}/gvc/${context.gvc}`;
    gvc.value = context.gvc;
    gvc.link = link;
  }

  return {
    ...getDefaultBaseInput(),
    version: 0,
    id: "",
    gvc: gvc,
    snapshots: {
      isEnabled: false,
      retentionDuration: {
        amount: df.snapshots.retentionDuration.amount.default,
        unit: df.snapshots.retentionDuration.unit.default as any,
      },
      schedule: "",
    },
    autoscaling: {
      isEnabled: false,
      maxCapacity: "",
      minFreePercentage: "",
      scalingFactor: "",
      predictive: {
        isEnabled: false,
        lookbackHours: "24",
        projectionHours: "6",
        minDataPoints: "10",
        minGrowthRateGBPerHour: "0.01",
        scalingFactor: "",
      },
    },
    mountOptions: {
      minMemory: {
        amount: df.mountOptions.resources.minMemory.amount.default,
        unit: df.mountOptions.resources.minMemory.unit.default as any,
      },
      maxMemory: {
        amount: df.mountOptions.resources.maxMemory.amount.default,
        unit: df.mountOptions.resources.maxMemory.unit.default as any,
      },
      minCpu: {
        amount: df.mountOptions.resources.minCpu.amount.default,
        unit: df.mountOptions.resources.minCpu.unit.default as any,
      },
      maxCpu: {
        amount: df.mountOptions.resources.maxCpu.amount.default,
        unit: df.mountOptions.resources.maxCpu.unit.default as any,
      },
    },
    fileSystemType: df.fileSystemType.default as any,
    initialCapacity: df.initialCapacity.default,
    performanceClass: df.performanceClass.default,
    storageClassSuffix: "",
    customEncryption: [],
    meta: {
      isEdit: false,
    },
  };
}

export function inputToVolumeSet(input: VolumeSetInput): VolumeSetCreate {
  const res: VolumeSetCreate = {
    name: input.name,
    kind: "volumeset",
    tags: inputToTags(input.tags),
    spec: {
      fileSystemType: input.fileSystemType,
      initialCapacity: Number(input.initialCapacity),
    },
  };

  if (input.description) {
    res.description = input.description;
  }

  if (input.storageClassSuffix) {
    res.spec.storageClassSuffix = input.storageClassSuffix;
  }

  if (input.fileSystemType === "shared") {
    res.spec.mountOptions = {
      resources: {},
    };
    if (input.mountOptions.minMemory.amount) {
      res.spec.mountOptions.resources!.minMemory = partsToMemory(input.mountOptions.minMemory);
    }
    if (input.mountOptions.maxMemory.amount) {
      res.spec.mountOptions.resources!.maxMemory = partsToMemory(input.mountOptions.maxMemory);
    }
    if (input.mountOptions.minCpu.amount) {
      res.spec.mountOptions.resources!.minCpu = partsToCpu(input.mountOptions.minCpu);
    }
    if (input.mountOptions.maxCpu.amount) {
      res.spec.mountOptions.resources!.maxCpu = partsToCpu(input.mountOptions.maxCpu);
    }
  } else {
    res.spec.performanceClass = input.performanceClass;
    res.spec.snapshots = {
      retentionDuration: `${input.snapshots.retentionDuration.amount}${input.snapshots.retentionDuration.unit}`,
    };
    if (input.snapshots.isEnabled) {
      res.spec.snapshots.schedule = input.snapshots.schedule;
    }
  }

  if (input.autoscaling.isEnabled) {
    res.spec.autoscaling = {};
    if (input.autoscaling.minFreePercentage) {
      res.spec.autoscaling.minFreePercentage = Number(input.autoscaling.minFreePercentage);
    }
    if (input.autoscaling.maxCapacity) {
      res.spec.autoscaling.maxCapacity = Number(input.autoscaling.maxCapacity);
    }
    if (input.autoscaling.scalingFactor) {
      res.spec.autoscaling.scalingFactor = Number(input.autoscaling.scalingFactor);
    }
    if (input.autoscaling.predictive.isEnabled) {
      res.spec.autoscaling.predictive = { enabled: true };
      if (input.autoscaling.predictive.lookbackHours) {
        res.spec.autoscaling.predictive.lookbackHours = Number(input.autoscaling.predictive.lookbackHours);
      }
      if (input.autoscaling.predictive.projectionHours) {
        res.spec.autoscaling.predictive.projectionHours = Number(input.autoscaling.predictive.projectionHours);
      }
      if (input.autoscaling.predictive.minDataPoints) {
        res.spec.autoscaling.predictive.minDataPoints = Number(input.autoscaling.predictive.minDataPoints);
      }
      if (input.autoscaling.predictive.minGrowthRateGBPerHour) {
        res.spec.autoscaling.predictive.minGrowthRateGBPerHour = Number(
          input.autoscaling.predictive.minGrowthRateGBPerHour,
        );
      }
      if (input.autoscaling.predictive.scalingFactor) {
        res.spec.autoscaling.predictive.scalingFactor = Number(input.autoscaling.predictive.scalingFactor);
      }
    }
  }

  if (input.customEncryption.length > 0 && input.customEncryption.some((ce) => !!ce.name && ce.value)) {
    const regions: Record<string, { keyId: string }> = {};
    for (const customEncryption of input.customEncryption) {
      regions[customEncryption.name] = { keyId: customEncryption.value };
    }
    res.spec.customEncryption = {
      regions: regions,
    };
  }

  return res;
}

export function volumeSetToInput(volumeSet: VolumeSet): VolumeSetInput {
  const def = getDefaultVolumeSetInput();
  const res: VolumeSetInput = {
    ...def,
    version: volumeSet.version,
    id: volumeSet.id,
    name: volumeSet.name,
    description: volumeSet.description,
    tags: tagsToInput(volumeSet.tags),
    fileSystemType: volumeSet.spec.fileSystemType,
    performanceClass: volumeSet.spec.performanceClass as any,
    initialCapacity: String(volumeSet.spec.initialCapacity),
    storageClassSuffix: volumeSet.spec.storageClassSuffix || "",
    meta: {
      ...def.meta,
      isEdit: true,
    },
  };

  const selfLink = linksOf(volumeSet).self!;
  const { org, gvc } = parseLink(selfLink, { useInputCtx: true });
  res.gvc.value = gvc;
  res.gvc.link = `/org/${org}/gvc/${gvc}`;

  if (volumeSet.spec.fileSystemType === "shared") {
    if (volumeSet.spec.mountOptions?.resources?.minMemory) {
      res.mountOptions.minMemory = memoryToParts(volumeSet.spec.mountOptions.resources.minMemory);
    }
    if (volumeSet.spec.mountOptions?.resources?.maxMemory) {
      res.mountOptions.maxMemory = memoryToParts(volumeSet.spec.mountOptions.resources.maxMemory);
    }
    if (volumeSet.spec.mountOptions?.resources?.minCpu) {
      res.mountOptions.minCpu = cpuToParts(volumeSet.spec.mountOptions.resources.minCpu);
    }
    if (volumeSet.spec.mountOptions?.resources?.maxCpu) {
      res.mountOptions.maxCpu = cpuToParts(volumeSet.spec.mountOptions.resources.maxCpu);
    }
  } else {
    res.performanceClass = volumeSet.spec.performanceClass!;
    if (volumeSet.spec.snapshots?.schedule) {
      res.snapshots.isEnabled = true;
      res.snapshots.schedule = volumeSet.spec.snapshots.schedule;
    }
    if (volumeSet.spec.snapshots?.retentionDuration) {
      const value = volumeSet.spec.snapshots!.retentionDuration.slice(0, -1);
      const unit = volumeSet.spec.snapshots!.retentionDuration.slice(-1);
      res.snapshots.retentionDuration = {
        amount: value,
        unit: unit as any,
      };
    }
  }

  if (Object.keys(volumeSet.spec.autoscaling || {}).length > 0) {
    res.autoscaling.isEnabled = true;
    if (volumeSet.spec.autoscaling?.maxCapacity !== undefined) {
      res.autoscaling.maxCapacity = String(volumeSet.spec.autoscaling?.maxCapacity);
    }
    if (volumeSet.spec.autoscaling?.minFreePercentage !== undefined) {
      res.autoscaling.minFreePercentage = String(volumeSet.spec.autoscaling?.minFreePercentage);
    }
    if (volumeSet.spec.autoscaling?.scalingFactor !== undefined) {
      res.autoscaling.scalingFactor = String(volumeSet.spec.autoscaling?.scalingFactor);
    }
    if (volumeSet.spec.autoscaling?.predictive) {
      const p = volumeSet.spec.autoscaling.predictive;
      if (p.enabled) {
        res.autoscaling.predictive.isEnabled = true;
      }
      if (p.lookbackHours !== undefined) res.autoscaling.predictive.lookbackHours = String(p.lookbackHours);
      if (p.projectionHours !== undefined) res.autoscaling.predictive.projectionHours = String(p.projectionHours);
      if (p.minDataPoints !== undefined) res.autoscaling.predictive.minDataPoints = String(p.minDataPoints);
      if (p.minGrowthRateGBPerHour !== undefined)
        res.autoscaling.predictive.minGrowthRateGBPerHour = String(p.minGrowthRateGBPerHour);
      if (p.scalingFactor !== undefined) res.autoscaling.predictive.scalingFactor = String(p.scalingFactor);
    }
  }

  if (Object.keys(volumeSet.spec.customEncryption?.regions || {}).length > 0) {
    const regions = volumeSet.spec.customEncryption?.regions || {};
    for (const region of Object.keys(regions)) {
      const keyId = regions[region].keyId;
      res.customEncryption.push({ id: uuidv4(), name: region, value: keyId });
    }
  }

  return res;
}

export function volumeSetToPatch(_volumeSet: VolumeSetCreate, concurrency: OptimisticConcurrency) {
  const volumeSet: any = JSON.parse(JSON.stringify(_volumeSet));

  volumeSet[`$replace/spec`] = volumeSet.spec;
  delete volumeSet.spec;

  volumeSet[`$replace/tags`] = volumeSet.tags;
  delete volumeSet.tags;

  volumeSet.id = concurrency.id;
  volumeSet.version = concurrency.version;

  return volumeSet;
}

export type NumberString = string;
export interface CpuParts {
  amount: NumberString;
  unit: CPUUnit;
}
export type CPUUnit = "m" | "";
export function cpuToParts(cpu: string | undefined): CpuParts {
  if (!cpu) {
    cpu = "";
  }
  const millicore: CPUUnit = "m";
  if (cpu.endsWith(millicore)) {
    const amount = cpu.split(millicore)[0];
    return { amount: amount, unit: "m" };
  } else {
    return { amount: cpu, unit: "" };
  }
}
export function partsToCpu(parts: CpuParts): string {
  const { unit, amount } = parts;
  return unit === "" ? amount : `${amount}m`;
}

export interface MemoryParts {
  amount: NumberString;
  unit: MemoryUnit;
}
export type MemoryUnit = "Mi" | "Gi" | "k8s";
export function memoryToParts(memory: string | undefined): MemoryParts {
  if (!memory) {
    memory = "";
  }
  const validUnits: MemoryUnit[] = ["Mi", "Gi"];
  if (memory.includes("e")) {
    return { amount: memory, unit: "k8s" };
  }
  for (let unit of validUnits) {
    if (memory.endsWith(unit)) {
      try {
        let amountStr = memory.split(unit)[0];
        let amount = Number(amountStr);
        if (amountStr.includes(".") && unit !== "Gi") {
          return { amount: memory, unit: "k8s" };
        }
        if (amountStr.includes(".") && unit === "Gi") {
          const whole = Number(amountStr.split(".")[0]);
          const fraction = Number(amountStr.split(".")[1]);
          return { amount: String(whole * 1_000 + fraction * 100), unit: "Mi" };
        }
        return { amount: String(amount), unit: unit };
      } catch (e) {
        return { amount: memory, unit: "k8s" };
      }
    }
  }
  return { amount: "", unit: "k8s" };
}
export function partsToMemory(parts: MemoryParts): string {
  const { amount, unit } = parts;

  if (unit === "k8s") {
    return amount;
  }
  if (amount) {
    return `${amount}${unit}`;
  }
  return "";
}

export const FileSystemTypes = [
  {
    name: "ext4",
    accessMode: "read-write-once",
    commandsSupported: [
      "expandVolume",
      "shrinkVolume",
      "deleteVolume",
      "restoreVolume",
      "createVolumeSnapshot",
      "deleteVolumeSnapshot",
    ],
  },
  {
    name: "xfs",
    accessMode: "read-write-once",
    commandsSupported: [
      "expandVolume",
      "shrinkVolume",
      "deleteVolume",
      "restoreVolume",
      "createVolumeSnapshot",
      "deleteVolumeSnapshot",
    ],
  },
  {
    name: "shared",
    accessMode: "read-write-many",
    commandsSupported: ["expandVolume", "shrinkVolume"],
  },
];
